class QuickView extends HTMLElement {
  static stylesAdded = false;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          position: absolute;
          width: 100%;
          padding: 10px;
          z-index: 2;
          bottom: 0;
          left: 0;
        }

        .quick-view-trigger {
          width: 100%;
          background: #FFFFFF;
          border: 1px solid #ECECEC;
          box-shadow: 0px 4px 4px 0px #DCDCDC40;
          border-radius: 3px;
          padding: 10px 20px;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }
          
        .quick-view-text {  
          font-family: caecilia-sans-text, Arial, sans-serif;
          font-weight: 700;
          font-size: 14px;
          line-height: 100%;
          text-align: center;
        }

        .quick-view-icon {
          display: none;
          width: 34px;
          height: 34px;
        }
        
        .quick-view-trigger:hover {
          background-color: #F5F5F5;
        }

        @media(max-width: 768px) {
          :host {
            position: absolute;
            left: auto;
            right: 0;

            width: 34px;
            height: 34px;
            padding: 0 5px 5px 0;
            box-sizing: content-box !important;
          }

          .quick-view-trigger {
            width: 100%;
            height: 100%;
            padding: 5px;
          }

          .quick-view-icon {
            width: auto;
            height: 100%;
            display: block;
          }

          .quick-view-text {
            display: none;
          }
        }
      </style>

      <button class="quick-view-trigger" aria-label="Quick view product">
        <span class="quick-view-text">
          Quick View
        </span>
        <img class="quick-view-icon" src="https://cdn.shopify.com/s/files/1/0635/0526/8968/files/icon-bag.png?v=1744205785" alt="" aria-hidden="true" />
      </button>
    `;

    this.scrollY = 0;
    this.ensureSharedStyles();

    this.trigger = this.shadowRoot.querySelector('.quick-view-trigger');
    this.modal = null;
    this.uniqueId = this.generateUniqueId();

    this.showQuickView = this.showQuickView.bind(this);
    this.hideQuickView = this.hideQuickView.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);

    this.productUrl = this.getAttribute('product-url');
    this.variantId = null;

    this.handleTriggerClick = this.handleTriggerClick.bind(this);
  }

  // GA4 Analytics Functions
  initializeDataLayer() {
    if (typeof window.dataLayer === 'undefined') {
      window.dataLayer = [];
    }
  }

  extractProductData() {
    try {
      const productId = this.getAttribute('product-id');
      const additionalDataScript = document.querySelector(`script[id="additionalVariantData-${productId}"]`);
      if (additionalDataScript) {
        const additionalData = JSON.parse(additionalDataScript.textContent);
        
        const currentVariantInput = this.modal.querySelector('input[name="id"]');
        const currentVariantId = currentVariantInput ? currentVariantInput.value : null;
        
        const productTitle = this.modal.querySelector('.product__title')?.textContent?.trim();
        const priceElement = this.modal.querySelector('.price-item--regular, .price__regular .price-item');
        let price = 0;
        
        if (priceElement) {
          const priceText = priceElement.textContent.replace(/[$,]/g, '');
          price = parseFloat(priceText) || 0;
        }

        let currentVariantData = null;
        if (currentVariantId && additionalData[currentVariantId]) {
          currentVariantData = additionalData[currentVariantId];
        }

        // Extract category from productType
        let category = '';
        if (additionalData.productType) {
          if (additionalData.productType.includes('>')) {
            // Split by ">" and take the last part
            const categoryParts = additionalData.productType.split('>');
            category = categoryParts.pop().trim();
          } else {
            // Use the whole productType if no ">" found
            category = additionalData.productType.trim();
          }
        }

        return {
          item_name: productTitle || '',
          item_id: currentVariantData?.sku || currentVariantId || '',
          price: price,
          quantity: '1',
          item_category: category,
          item_brand: 'Brighton'
        };
      }
    } catch (error) {
      console.error('Error extracting product data for analytics:', error);
    }
    
    return null;
  }

  trackEvent(eventName, customParams = {}) {
    this.initializeDataLayer();
    
    const baseParams = {
      event: eventName,
      page_section: 'Quick View'
    };

    const eventData = { ...baseParams, ...customParams };
    
    window.dataLayer.push(eventData);
    console.log('GA4 Quick View Event Tracked:', eventData);
  }

  trackViewItem() {
    const productData = this.extractProductData();
    if (!productData) return;

    this.trackEvent('view_item', {
      ecommerce: {
        items: [productData]
      }
    });
  }

  trackAddToWishlist() {
    const productData = this.extractProductData();
    if (!productData) return;

    this.trackEvent('add_to_wishlist', {
      ecommerce: {
        items: [productData]
      }
    });
  }

  trackClickViewFullDetails() {
    const productData = this.extractProductData();
    if (!productData) return;

    this.trackEvent('click_view_full_details', {
      ecommerce: {
        items: [productData]
      }
    });
  }

  trackSelectProductOption(selectionType, selectionValue) {
    const productData = this.extractProductData();
    if (!productData) return;

    this.trackEvent('select_product_option', {
      selection_type: selectionType,
      selection_value: selectionValue,
      custom_product_sku: productData.item_id,
      ecommerce: {
        items: [productData]
      }
    });
  }

  trackClickExitPopup() {
    this.trackEvent('click_exit_popup');
  }

  trackClickImageGallery() {
    this.trackEvent('click_image_gallery');
  }

  ensureSharedStyles() {
    if (!QuickView.stylesAdded) {
      const modalStyles = document.createElement('style');
      modalStyles.id = 'quick-view-modal-styles';
      modalStyles.textContent = `
        .quick-view-box {
          display: flex;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0);
          z-index: 99999999;
          justify-content: center;
          align-items: center;
          opacity: 0;
          pointer-events: none;
          transition: background-color 0.3s ease, opacity 0.3s ease;
          padding: 30px;
        }
        
        .quick-view-box.active {
          background-color: rgba(0, 0, 0, 0.5);
          opacity: 1;
          pointer-events: auto;
        }
        
        .quick-view-box-container {
          background: white;
          max-width: 990px;
          max-height: 95vh;
          max-height: 95dvh;
          width: 100%;
          border-radius: 4px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
          overflow-y: auto;
          position: relative;
          padding: 0 34px 34px 34px;
          transform: translateY(20px);
          opacity: 0;
          transition: transform 0.3s ease-in-out, opacity 0.3s ease;
        }

        .quick-view-box-header {
          position: sticky;
          top: 0;
          z-index: 10;

          background-color: #ffffff;
          padding: 10px;
          display: flex;
          justify-content: flex-end;
        }

        @media(max-width: 768px) {
          .quick-view-box-container {
            transform: translateY(100%);
            transition: transform 0.5s ease-in-out, opacity 0.3s ease;
          }
        }
        
        .quick-view-box.active .quick-view-box-container {
          transform: translateY(0);
          opacity: 1;
        }

        .quick-view-box-close {
          background: none;
          border: none;
          cursor: pointer;
          padding: 5px;
          font-weight: bold;
          font-size: 16px;
          position: relative;
          transform: translateX(100%);
        }
        
        .quick-view-box-close:hover {
          opacity: 0.7;
        }

        @media(max-width: 768px) {
          .quick-view-box {
            padding: 0;
            align-items: flex-end;
          }

          .quick-view-box-container {
            padding: 0 11px 24px 11px;
          }

          .quick-view-box-close {
            transform: translateX(50%);
          }

          .quick-view-box-close svg {
            width: 14px;
            height: 14px;
          }
        }
        
        .quick-view-loading {
          width: 100%;
          display: flex;
          min-height: 500px;
        }
        
        .skeleton-wrapper {
          display: flex;
          gap: 50px;
          width: 100%;
        }
        
        .skeleton-image {
          width: 500px;
          flex-shrink: 0;
          background: #f0f0f0;
          position: relative;
          overflow: hidden;
        }

        @media(max-width: 991px) {
          .skeleton-image {
            width: 370px;
          }
        }
        
        .skeleton-content {
          width: calc(100% - 500px);
          padding-top: 40px;
        }

        @media(max-width: 768px) {
          .skeleton-wrapper {
            flex-direction: column;
            gap: 28px;
          }

          .skeleton-image {
            width: 100%;
          }

          .skeleton-image::before {
            content: "";
            display: block;
            padding-top: 100%;
            width: 100%;
          }

          .skeleton-content {
            width: 100%;
            padding-top: 0;
          }
        } 
        
        .skeleton-title,
        .skeleton-price,
        .skeleton-text,
        .skeleton-button {
          background: #f0f0f0;
          border-radius: 4px;
          margin-bottom: 15px;
          position: relative;
          overflow: hidden;
        }
        
        .skeleton-title {
          height: 28px;
          width: 70%;
        }
        
        .skeleton-price {
          height: 22px;
          width: 40%;
        }
        
        .skeleton-text {
          height: 14px;
          margin-bottom: 10px;
        }
        
        .skeleton-text:nth-child(odd) {
          width: 100%;
        }
        
        .skeleton-text:nth-child(even) {
          width: 90%;
        }
        
        .skeleton-button {
          height: 42px;
          width: 80%;
          margin-top: 20px;
        }
        
        .skeleton-image::after,
        .skeleton-title::after,
        .skeleton-price::after,
        .skeleton-text::after,
        .skeleton-button::after {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
          animation: skeleton-loading 1.5s infinite;
        }
        
        @keyframes skeleton-loading {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .quick-view-error {
          padding: 20px;
          color: red;
          text-align: center;
        }
      `;

      document.head.appendChild(modalStyles);
      QuickView.stylesAdded = true;
    }
  }

  static get observedAttributes() {
    return ['product-url'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'product-url' && oldValue !== newValue) {
      this.productUrl = newValue;
    }
  }

  connectedCallback() {
    if (this.productUrl?.includes('digital-gift-card')) {
      this.remove();
    }

    this.trigger.addEventListener('click', this.handleTriggerClick);

    document.addEventListener('addToCart', () => {
      this.hideQuickView();
    });
  }

  handleTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();

    // Set Variant Id
    const variantId = this.getVariantId();

    if (variantId) {
      this.variantId = variantId;
    }

    this.showQuickView();
  }

  disconnectedCallback() {
    this.trigger.removeEventListener('click', this.handleTriggerClick);
    document.removeEventListener('keydown', this.handleKeyDown);
    this.cleanupExternalElements();
    document.body.style.removeProperty('overflow')
  }

  getVariantId() {
    const nsProduct = this.closest(".ns-product") || this.closest(".ns-result");
    if (!nsProduct) return;

    let selectedSwatch = nsProduct.querySelector(".nosto-selected-swatch");
    if (selectedSwatch) {
      return selectedSwatch.dataset.skuId;
    }

    selectedSwatch = nsProduct.querySelector(".ns-product__swatch-input:checked");
    if (selectedSwatch) {
      return selectedSwatch.value;
    }

    return null;
  }

  generateUniqueId() {
    return 'qv-' + Math.random().toString(36).substring(2, 9);
  }

  handleOutsideClick(event) {
    if (event.target === this.modal) {
      this.trackClickExitPopup();
      this.hideQuickView();
    }
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      this.trackClickExitPopup();
      this.hideQuickView();
    }
  }

  cleanupExternalElements(hideModal = true) {
    if (this.modal && document.body.contains(this.modal) && hideModal) {
      this.unlockBodyScroll();

      document.body.removeChild(this.modal);
      this.modal = null;
    }

    const filesHolder = document.getElementById(`product-detail-files-holder-${this.uniqueId}`);
    if (filesHolder) {
      filesHolder.remove();
    }

    document.removeEventListener('keydown', this.handleKeyDown);
  }

  createModal() {
    this.modal = document.createElement('div');
    this.modal.className = 'quick-view-box';
    this.modal.id = `quick-view-box-${this.uniqueId}`;
    this.modal.setAttribute('role', 'dialog');
    this.modal.setAttribute('aria-modal', 'true');
    this.modal.innerHTML = `
      <div class="quick-view-box-container">
        <div class="quick-view-box-header">
          <button class="quick-view-box-close" aria-label="Close quick view">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M1 18.5L10 9.5L18.5 1" stroke="black" stroke-width="2" stroke-linecap="round"/>
              <path d="M1 1L18.5 18.5" stroke="black" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <div class="quick-view-box-content"></div>
      </div>
    `;

    const closeBtn = this.modal.querySelector('.quick-view-box-close');
    closeBtn.addEventListener('click', () => {
      this.trackClickExitPopup();
      this.hideQuickView();
    });
    this.modal.addEventListener('click', this.handleOutsideClick);

    document.body.appendChild(this.modal);

    this.modal.offsetWidth;
  }

  async showQuickView() {
    this.lockBodyScroll();

    if (!this.productUrl) {
      console.error('No product URL provided');
      return;
    }

    if (!this.modal) {
      this.createModal();
    }

    const contentArea = this.modal.querySelector('.quick-view-box-content');
    contentArea.innerHTML = `
      <div class="quick-view-loading">
        <div class="skeleton-wrapper">
          <div class="skeleton-image">&nbsp;</div>
          <div class="skeleton-content">
            <div class="skeleton-title">&nbsp;</div>
            <div class="skeleton-price">&nbsp;</div>
            <div class="skeleton-text">&nbsp;</div>
            <div class="skeleton-text">&nbsp;</div>
            <div class="skeleton-text">&nbsp;</div>
            <div class="skeleton-text">&nbsp;</div>
            <div class="skeleton-text">&nbsp;</div>
            <div class="skeleton-button">&nbsp;</div>
          </div>
        </div>
      </div>
    `;

    requestAnimationFrame(() => {
      this.modal.classList.add('active');
    });

    document.addEventListener('keydown', this.handleKeyDown);

    try {
      let url = this.productUrl;
      
      if (this.variantId) {
        url += "?variant=" + this.variantId;
      }

      const productTemplate = await this.getProductDetailsTemplate(url);

      const productFiles = productTemplate.querySelector('#product-detail-files');
      if (productFiles) {
        const filesHolder = document.createElement('div');
        filesHolder.id = `product-detail-files-holder-${this.uniqueId}`;
        filesHolder.style.display = 'none';

        const filesContent = productFiles.content.cloneNode(true);
        filesHolder.appendChild(filesContent);
        document.body.appendChild(filesHolder);


        const scripts = filesHolder.querySelectorAll('script');
        const scriptPromises = Array.from(scripts).map(script => {
          return new Promise((resolve, reject) => {
            const newScript = document.createElement('script');

            if (script.src) {
              newScript.src = script.src;
              newScript.type = script.type || 'text/javascript';
              newScript.id = script.id || '';
              newScript.async = false;

              newScript.onload = () => resolve();
              newScript.onerror = () => reject(new Error(`Failed to load script: ${script.src}`));
            } else {
              newScript.textContent = script.textContent;
              newScript.type = script.type || 'text/javascript';
              newScript.id = script.id || '';
              resolve();
            }

            script.parentNode.replaceChild(newScript, script);
          });
        });

        await Promise.all(scriptPromises).catch(error => {
          console.error('Error loading scripts:', error);
        });
      }

      const productContent = productTemplate.querySelector('#product-detail-content');
      if (productContent) {
        contentArea.innerHTML = '';

        const contentClone = productContent.content.cloneNode(true);
        contentArea.appendChild(contentClone);

        this.updatePrice();

        
        // Initialize Flickity Carousel for media list
        this.setupCarousel();
        this.initSwym();
        this.setupAnalyticsTracking();

        // Track view_item event
        setTimeout(() => {
          this.trackViewItem();
        }, 500);

        const availableVariantFound = productContent.content.querySelector('#availableVariantFound');
        if (availableVariantFound) {
          this.cleanupExternalElements(false);

          this.variantId = availableVariantFound.dataset.variantId;
          this.showQuickView();
        }
      } else {
        throw new Error('Product content not found');
      }

    } catch (error) {
      console.error('Error loading product details:', error);
      contentArea.innerHTML = `
          <div class="quick-view-error">
            <p>Failed to load product details. Please try again later.</p>
            <button class="retry-button">Retry</button>
          </div>
        `;

      // Add retry button functionality
      const retryButton = contentArea.querySelector('.retry-button');
      if (retryButton) {
        retryButton.addEventListener('click', () => {
          this.showQuickView();
        });
      }
    }

    setTimeout(() => {
      const closeButton = this.modal.querySelector('.quick-view-box-close');
      if (closeButton) closeButton.focus();
    }, 300);
  }

  updatePrice() {
    const $priceWrapper = this.modal.querySelector('.price-item');
    const productId = this.getAttribute('product-id');
    const additionalDataScript = document.querySelector(`script[id="additionalVariantData-${productId}"]`);
    if (!additionalDataScript) return;
    
    const parsedData = JSON.parse(additionalDataScript.textContent);
    const variantPrice = parsedData[this.variantId]?.options?.Amount;

    if ($priceWrapper && variantPrice) {
      $priceWrapper.innerText = variantPrice;
    }
  }


  setupAnalyticsTracking() {
    if (!this.modal) return;

    // Track wishlist clicks
    const wishlistButton = this.modal.querySelector('.swym-add-to-wishlist-view-product');
    if (wishlistButton) {
      wishlistButton.addEventListener('click', () => {
        this.trackAddToWishlist();
      });
    }

    // Track "View Full Details" link clicks
    const viewFullDetailsLink = this.modal.querySelector('.product-form__learn-more');
    if (viewFullDetailsLink) {
      viewFullDetailsLink.addEventListener('click', () => {
        this.trackClickViewFullDetails();
      });
    }

    // Track variant option changes
    const variantRadios = this.modal.querySelector('variant-radios');
    if (variantRadios) {
      const optionInputs = variantRadios.querySelectorAll('input[type="radio"]');
      optionInputs.forEach(input => {
        input.addEventListener('change', (event) => {
          const fieldset = event.target.closest('fieldset');
          const optionName = fieldset.dataset.optionName;
          const optionValue = event.target.value;
          
          this.trackSelectProductOption(optionName, optionValue);
        });
      });
    }
  }

  initSwym() {
    function swymCallbackFn(swat) {
      swat.initializeActionButtons(".quick-view-box-container");
    }

    if(!window.SwymCallbacks){
      window.SwymCallbacks = [];
    }

    window.SwymCallbacks.push(swymCallbackFn);
  }

  setupCarousel() {
    const contentArea = this.modal.querySelector('.quick-view-box-content');
    const mediaList = contentArea.querySelector('.product__media-list');
    if (mediaList) {
      const movePageDotsOutside = () => {
        const pageDots = mediaList.querySelector('.flickity-page-dots');
        if (pageDots && mediaList.parentNode) {
          // Move the page dots outside of the media list container
          mediaList.parentNode.insertBefore(pageDots, mediaList.nextSibling);
          
          // Add click tracking to dots after they're moved
          const dots = pageDots.querySelectorAll('.dot');
          dots.forEach(dot => {
            dot.addEventListener('click', () => {
              this.trackClickImageGallery();
            });
          });
        }
      };

      const createArrows = () => {
        mediaList.querySelector(".flickity-custom-pagination")?.remove();

        const customPagination = document.createElement('div');
        customPagination.className = 'flickity-custom-pagination';
        customPagination.innerHTML = `
            <button class="flickity-prev-next-button previous" type="button" aria-label="Previous">
              <svg width="20" height="36" viewBox="0 0 20 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M18.5 34.5L2 18L18 2" stroke="#4A4A4A" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <button class="flickity-prev-next-button next" type="button" aria-label="Next">
              <svg width="20" height="36" viewBox="0 0 20 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"">
                <path d="M2 2L18.5 18.5L2.5 34.5" stroke="#4A4A4A" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          `;

        mediaList.appendChild(customPagination);

        const prevButton = customPagination.querySelector('.previous');
        const nextButton = customPagination.querySelector('.next');

        prevButton.addEventListener('click', () => {
          flkty.previous();
          this.trackClickImageGallery();
        });

        nextButton.addEventListener('click', () => {
          flkty.next();
          this.trackClickImageGallery();
        });
      }

      const flickityOptions = {
        cellAlign: 'left',
        contain: true,
        pageDots: true,
        prevNextButtons: false,
        wrapAround: true,
        cellSelector: '.product__media-item.is-visible',
        arrowShape: 'M18.5 34.5L2 18L18 2',
        on: {
          ready: function() {
            movePageDotsOutside();
            createArrows();
          }
        }
      };

      let flkty = new Flickity(mediaList, flickityOptions);

      const variantRadios = contentArea.querySelector("variant-radios");
      if (variantRadios) {
        variantRadios.addEventListener('variantChange', (event) => {
          if (event.detail?.target?.name?.toLowerCase()?.includes('size')) {
            return;
          }

          setTimeout(() => {
            const previousDots = mediaList.closest('media-gallery')?.querySelector('.flickity-page-dots');
            if (previousDots) {
              mediaList.appendChild(previousDots);
            }

            flkty.destroy();
            flkty = new Flickity(mediaList, flickityOptions);
          }, 300);
        });
      }

      
    }
  }

  lockBodyScroll() {
    this.scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  unlockBodyScroll() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, this.scrollY);
  }

  hideQuickView() {
    if (this.modal) {
      this.modal.classList.remove('active');

      setTimeout(() => {
        if (this.modal && document.body.contains(this.modal)) {
          this.unlockBodyScroll();

          this.cleanupExternalElements();

          this.trigger.focus();
        }
      }, 300);
    }
  }

  async getProductDetailsTemplate(productUrl) {
    try {
      const productUrlWithParams = new URL(productUrl);
      const searchParams = productUrlWithParams.searchParams;
      searchParams.append('section_id', 'product-detail-quick-view');

      const response = await fetch(productUrlWithParams.toString());

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.text();
      const parser = new DOMParser();
      return parser.parseFromString(data, 'text/html');


    } catch (error) {
      console.error('Error fetching product details:', error);
      throw error;
    }
  }

  getVariantIdFromAnchor(anchorElement) {
    const href = anchorElement.getAttribute('href');
    const url = new URL(href, window.location.origin);
    return url.searchParams.get('variant');
  }
}

customElements.define('quick-view', QuickView);